import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { cn } from '@/lib/cn';
import {
  type CreateDataSourceRequest,
  CreateDataSourceRequestSchema,
  DATA_SOURCE_DEFAULT_PORT,
  type DataSourceKind,
  SUPPORTED_DATA_SOURCE_KINDS,
  type UpdateDataSourceRequest,
  UpdateDataSourceRequestSchema,
  isDataSourceKindSupported,
} from '@innermate/shared';
import { X } from 'lucide-react';
import { type KeyboardEvent, useMemo, useState } from 'react';
import { ConnectionTestPanel } from './ConnectionTestPanel';

type Mode = 'create' | 'edit';

interface DataSourceFormProps {
  initial?: Partial<CreateDataSourceRequest>;
  submitLabel: string;
  submitting?: boolean;
  onSubmit: (body: CreateDataSourceRequest | UpdateDataSourceRequest) => void;
  onCancel?: () => void;
  /** 수정 시에는 kind 변경 금지. */
  kindLocked?: boolean;
  mode: Mode;
  /** mode === 'edit' 일 때 필수 — test-connection-by-id 에 사용. */
  dataSourceId?: string;
}

const DEFAULT_FORM: CreateDataSourceRequest = {
  name: '',
  kind: 'postgres',
  host: 'localhost',
  port: DATA_SOURCE_DEFAULT_PORT.postgres,
  database: '',
  username: '',
  password: '',
  sslMode: 'require',
  schemaFilter: ['public'],
  extraConfig: {},
  description: null,
};

const KIND_LABELS: Record<DataSourceKind, string> = {
  postgres: 'PostgreSQL',
  mysql: 'MySQL',
  mariadb: 'MariaDB',
  mssql: 'Microsoft SQL Server',
  oracle: 'Oracle',
  bigquery: 'BigQuery',
  excel: 'Excel·CSV',
  tableau: 'Tableau Server',
  duckdb: 'DuckDB (자동)',
};

type TestStatus = 'idle' | 'ok' | 'fail';

export function DataSourceForm({
  initial,
  submitLabel,
  submitting,
  onSubmit,
  onCancel,
  kindLocked,
  mode,
  dataSourceId,
}: DataSourceFormProps) {
  const [form, setForm] = useState<CreateDataSourceRequest>({
    ...DEFAULT_FORM,
    ...initial,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [testStatus, setTestStatus] = useState<TestStatus>('idle');
  const [resetToken, setResetToken] = useState(0);

  const supportNotice = useMemo(
    () =>
      isDataSourceKindSupported(form.kind)
        ? null
        : `${KIND_LABELS[form.kind]} 는 아직 지원되지 않습니다 (준비 중).`,
    [form.kind],
  );

  const invalidateTest = () => {
    setTestStatus('idle');
    setResetToken((v) => v + 1);
  };

  const update = <K extends keyof CreateDataSourceRequest>(
    key: K,
    value: CreateDataSourceRequest[K],
  ) => {
    setForm((f) => ({ ...f, [key]: value }));
    invalidateTest();
  };

  const onKindChange = (kind: DataSourceKind) => {
    setForm((f) => ({
      ...f,
      kind,
      port: DATA_SOURCE_DEFAULT_PORT[kind],
      // postgres 만 public 기본. 그 외엔 비워 어댑터 기본(접속 DB/owner/dbo) 사용.
      schemaFilter: kind === 'postgres' ? ['public'] : [],
    }));
    invalidateTest();
  };

  const extra = (form.extraConfig ?? {}) as Record<string, unknown>;
  const setExtra = (patch: Record<string, unknown>) => {
    setForm((f) => ({ ...f, extraConfig: { ...((f.extraConfig ?? {}) as object), ...patch } }));
    invalidateTest();
  };
  const oracleVia: 'service' | 'sid' =
    typeof extra.sid === 'string' && extra.sid ? 'sid' : 'service';

  const buildPayload = (): CreateDataSourceRequest | UpdateDataSourceRequest | null => {
    if (mode === 'create') {
      const parsed = CreateDataSourceRequestSchema.safeParse(form);
      if (!parsed.success) {
        const next: Record<string, string> = {};
        for (const issue of parsed.error.issues) {
          const key = String(issue.path[0] ?? '_');
          if (!next[key]) next[key] = issue.message;
        }
        setErrors(next);
        return null;
      }
      setErrors({});
      return parsed.data;
    }
    // edit: password 가 빈 값이면 제외 → 서버가 기존 비번 유지.
    const { kind: _kind, password, ...rest } = form;
    const draft: Record<string, unknown> = password === '' ? rest : { ...rest, password };
    const parsed = UpdateDataSourceRequestSchema.safeParse(draft);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '_');
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return null;
    }
    setErrors({});
    return parsed.data;
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (testStatus !== 'ok') return;
    const payload = buildPayload();
    if (!payload) return;
    onSubmit(payload);
  };

  const getTestValues = () => buildPayload();

  const handleTestResult = (ok: boolean | null) => {
    if (ok === null) setTestStatus('idle');
    else setTestStatus(ok ? 'ok' : 'fail');
  };

  const submitDisabled = submitting || !!supportNotice || testStatus !== 'ok';

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <Field label="이름" htmlFor="ds-name" error={errors.name} hint="식별용. 공백 가능.">
          <Input
            id="ds-name"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="예: 운영 DB"
          />
        </Field>

        <Field
          label="DB 종류"
          htmlFor="ds-kind"
          error={errors.kind}
          hint={supportNotice ?? undefined}
        >
          <select
            id="ds-kind"
            value={form.kind}
            disabled={kindLocked}
            onChange={(e) => onKindChange(e.target.value as DataSourceKind)}
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm disabled:opacity-50"
          >
            {(Object.keys(KIND_LABELS) as DataSourceKind[]).map((k) => {
              const supported = SUPPORTED_DATA_SOURCE_KINDS.includes(k);
              return (
                <option key={k} value={k} disabled={!supported}>
                  {KIND_LABELS[k]}
                  {supported ? '' : ' (준비 중)'}
                </option>
              );
            })}
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-[1fr_100px] gap-4">
        <Field label="호스트" htmlFor="ds-host" error={errors.host}>
          <Input id="ds-host" value={form.host} onChange={(e) => update('host', e.target.value)} />
        </Field>
        <Field label="포트" htmlFor="ds-port" error={errors.port}>
          <Input
            id="ds-port"
            type="number"
            inputMode="numeric"
            value={form.port}
            onChange={(e) => update('port', Number(e.target.value) || 0)}
          />
        </Field>
      </div>

      <Field label="데이터베이스" htmlFor="ds-database" error={errors.database}>
        <Input
          id="ds-database"
          value={form.database}
          onChange={(e) => update('database', e.target.value)}
          placeholder="예: shop"
        />
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field label="사용자" htmlFor="ds-user" error={errors.username}>
          <Input
            id="ds-user"
            value={form.username}
            onChange={(e) => update('username', e.target.value)}
            autoComplete="off"
          />
        </Field>
        <Field
          label="비밀번호"
          htmlFor="ds-pass"
          error={errors.password}
          hint={mode === 'edit' ? '비워두면 기존 비밀번호를 유지합니다.' : '암호화되어 저장됩니다.'}
        >
          <PasswordInput
            id="ds-pass"
            value={form.password}
            onChange={(e) => update('password', e.target.value)}
            autoComplete="new-password"
            placeholder={mode === 'edit' ? '변경하지 않으려면 비워두세요' : undefined}
          />
        </Field>
      </div>

      <Field label="SSL 모드" htmlFor="ds-ssl" error={errors.sslMode}>
        <div className="flex gap-2">
          {(['disable', 'require', 'verify-full'] as const).map((m) => (
            <button
              type="button"
              key={m}
              onClick={() => update('sslMode', m)}
              className={cn(
                'h-9 px-3 rounded-md border text-xs',
                form.sslMode === m
                  ? 'border-accent bg-accent/10 text-foreground'
                  : 'border-border text-foreground-muted hover:text-foreground',
              )}
            >
              {m}
            </button>
          ))}
        </div>
      </Field>

      {form.kind === 'oracle' ? (
        <Field
          label="Oracle 접속 방식"
          htmlFor="ds-oracle-connect"
          hint="Service Name(권장) 또는 SID 중 하나. 비워두면 위 '데이터베이스' 값을 Service Name 으로 사용."
        >
          <div className="flex gap-2">
            <div className="flex gap-1">
              {(['service', 'sid'] as const).map((m) => (
                <button
                  type="button"
                  key={m}
                  onClick={() =>
                    m === 'sid'
                      ? setExtra({
                          sid: typeof extra.sid === 'string' ? extra.sid : '',
                          serviceName: undefined,
                        })
                      : setExtra({
                          serviceName:
                            typeof extra.serviceName === 'string' ? extra.serviceName : '',
                          sid: undefined,
                        })
                  }
                  className={cn(
                    'h-10 px-3 rounded-md border text-xs whitespace-nowrap',
                    oracleVia === m
                      ? 'border-accent bg-accent/10 text-foreground'
                      : 'border-border text-foreground-muted hover:text-foreground',
                  )}
                >
                  {m === 'service' ? 'Service Name' : 'SID'}
                </button>
              ))}
            </div>
            <Input
              id="ds-oracle-connect"
              value={
                oracleVia === 'sid' ? String(extra.sid ?? '') : String(extra.serviceName ?? '')
              }
              onChange={(e) =>
                oracleVia === 'sid'
                  ? setExtra({ sid: e.target.value })
                  : setExtra({ serviceName: e.target.value })
              }
              placeholder={oracleVia === 'sid' ? '예: ORCL' : '예: FREEPDB1'}
            />
          </div>
        </Field>
      ) : null}

      {form.kind === 'mssql' ? (
        <Field
          label="SQL Server 옵션"
          htmlFor="ds-mssql-opts"
          hint="사내 평문 접속이면 암호화를 끄세요."
        >
          <div className="flex flex-wrap gap-2">
            {(
              [
                ['encrypt', '연결 암호화(encrypt)'],
                ['trustServerCertificate', '서버 인증서 신뢰'],
              ] as const
            ).map(([key, label]) => {
              const on = extra[key] === true;
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => setExtra({ [key]: !on })}
                  className={cn(
                    'h-9 px-3 rounded-md border text-xs',
                    on
                      ? 'border-accent bg-accent/10 text-foreground'
                      : 'border-border text-foreground-muted hover:text-foreground',
                  )}
                >
                  {on ? '☑' : '☐'} {label}
                </button>
              );
            })}
          </div>
        </Field>
      ) : null}

      <Field
        label="스키마 필터"
        htmlFor="ds-schema-filter"
        error={errors.schemaFilter}
        hint={
          form.kind === 'oracle'
            ? '비우면 접속 계정(owner) 스키마만 sync. owner 명은 대문자. 엔터로 여러 개 추가.'
            : form.kind === 'mysql' || form.kind === 'mariadb'
              ? '비우면 접속 데이터베이스만 sync. 다른 DB 명을 엔터로 추가 가능.'
              : form.kind === 'mssql'
                ? '비우면 dbo 스키마만 sync. 엔터로 여러 개 추가.'
                : '빈 값이면 기본(public) 만 sync. 엔터로 여러 개 추가.'
        }
      >
        <SchemaChipInput
          values={form.schemaFilter}
          onChange={(next) => update('schemaFilter', next)}
        />
      </Field>

      <Field
        label="설명 (관리자용 메모)"
        htmlFor="ds-desc"
        error={errors.description}
        hint="LLM 에도 함께 전달되지는 않음. 테이블/컬럼별 설명은 등록 후 편집 가능."
      >
        <textarea
          id="ds-desc"
          value={form.description ?? ''}
          onChange={(e) =>
            update('description', e.target.value.length === 0 ? null : e.target.value)
          }
          rows={3}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus-visible:outline-none"
          placeholder="이 데이터소스의 용도를 적어주세요."
        />
      </Field>

      <div className="rounded-xl border border-border bg-surface-muted/30 p-4">
        <p className="mb-2 text-xs text-foreground-muted">
          연결을 먼저 테스트 한 뒤 저장하세요. 저장 시 구조 sync 가 백그라운드에서 시작됩니다.
        </p>
        <ConnectionTestPanel
          values={getTestValues}
          mode={mode}
          dataSourceId={dataSourceId}
          onResult={handleTestResult}
          resetToken={resetToken}
          disabledReason={supportNotice ?? undefined}
        />
      </div>

      <div className="flex flex-col items-end gap-2 pt-2">
        {testStatus !== 'ok' && !supportNotice ? (
          <p className="text-xs text-foreground-muted">
            {testStatus === 'fail'
              ? '연결 테스트가 실패했습니다. 값을 확인하고 다시 테스트하세요.'
              : '저장하려면 먼저 연결 테스트를 통과해야 합니다.'}
          </p>
        ) : null}
        <div className="flex gap-2">
          {onCancel ? (
            <Button type="button" variant="ghost" onClick={onCancel}>
              취소
            </Button>
          ) : null}
          <Button type="submit" disabled={submitDisabled}>
            {submitting ? '저장 중…' : submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
}

function SchemaChipInput({
  values,
  onChange,
}: {
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');

  const push = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.includes(v)) {
      setDraft('');
      return;
    }
    onChange([...values, v]);
    setDraft('');
  };

  const remove = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      push();
    } else if (e.key === 'Backspace' && draft.length === 0 && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div className="flex flex-wrap gap-1.5 items-center rounded-md border border-border bg-surface px-2 py-1.5 min-h-10">
      {values.map((v, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: 같은 도메인 문자열이 중복될 수 있어 index 를 함께 사용
          key={`${v}-${i}`}
          className="inline-flex items-center gap-1 rounded-sm bg-surface-muted px-2 h-6 text-xs font-mono"
        >
          {v}
          <button
            type="button"
            onClick={() => remove(i)}
            aria-label={`${v} 제거`}
            className="text-foreground-muted hover:text-foreground"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <input
        id="ds-schema-filter"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={push}
        placeholder={values.length === 0 ? 'public' : ''}
        className="flex-1 min-w-[80px] bg-transparent text-sm focus-visible:outline-none"
      />
    </div>
  );
}
